/**
 * Account setting: the "Built with Molecule.dev" badge default for this
 * user's deploys. ON by default; a paying user can turn it off account-wide
 * (per-app overrides live in each project's Pipeline panel). A FREE user
 * cannot hide the badge — the API refuses the write with
 * `403 requiresUpgrade` and this section calls {@link AccountBadgeSectionProps.onUpgrade}
 * so the parent opens the plans modal instead of leaving a dead toggle. The
 * deploy-time rule (`runtime/attribution.ts`) ignores a free account's
 * opt-out anyway, so a downgrade forces the badge back on without touching
 * the stored choice.
 */
import { type JSX, useEffect, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Switch } from '@molecule/app-ui-react'

import { emitNotification } from '../notifications.js'

export interface AccountBadgeSectionProps {
  /** Whether the signed-in user is on a paid plan (the toggle's gate). */
  isPaidPlan: boolean
  /** Opens the plans modal (paid-only action surface for free users). */
  onUpgrade: () => void
}

/**
 * The account-wide badge default switch.
 *
 * @param props - Plan gate + the parent's upgrade affordance.
 * @returns The row.
 */
export function AccountBadgeSection({
  isPaidPlan,
  onUpgrade,
}: AccountBadgeSectionProps): JSX.Element {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  const [badgeEnabled, setBadgeEnabled] = useState<boolean>(true)
  const [saving, setSaving] = useState(false)

  // A failed read leaves the platform default (on) showing — the same side
  // the deploy rule falls back to.
  useEffect(() => {
    let cancelled = false
    http
      .get<{ attributionBadge?: boolean }>('/account/attribution-badge')
      .then((res) => {
        if (!cancelled && typeof res.data?.attributionBadge === 'boolean') {
          setBadgeEnabled(res.data.attributionBadge)
        }
      })
      .catch((_error: unknown) => {
        // The default (on) stands; nothing actionable in the panel.
      })
    return () => {
      cancelled = true
    }
  }, [http])

  const patch = (show: boolean): void => {
    setBadgeEnabled(show)
    setSaving(true)
    http
      .patch('/account/attribution-badge', { attributionBadge: show })
      .then(() => {
        setSaving(false)
      })
      .catch((error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status
        setBadgeEnabled(!show)
        setSaving(false)
        if (status === 403) {
          // Free account: the write is refused with requiresUpgrade — open
          // the plans modal instead of a toggle that silently does nothing.
          onUpgrade()
        } else {
          emitNotification(
            t('settings.badge.saveFailed', undefined, {
              defaultValue: 'Could not save the badge setting.',
            }),
            'error',
          )
        }
      })
  }

  const text = (
    <div style={{ minWidth: 0 }}>
      <div className={cm.cn(cm.textSize('sm'), cm.fontWeight('medium'))}>
        {t('settings.badge.label', undefined, {
          defaultValue: '“Built with Molecule.dev” badge',
        })}
      </div>
      <div className={cm.cn(cm.textSize('sm'), cm.textMuted)}>
        {t('settings.badge.desc', undefined, {
          defaultValue:
            'Default for apps you deploy. Each app can override it in its Pipeline panel.',
        })}
      </div>
    </div>
  )

  if (isPaidPlan) {
    return (
      <Flex align="center" justify="between" gap="sm">
        {text}
        <Switch
          data-mol-id="settings-badge-switch"
          checked={badgeEnabled}
          disabled={saving}
          aria-label={t('settings.badge.label', undefined, {
            defaultValue: '“Built with Molecule.dev” badge',
          })}
          onChange={(e) => patch((e.target as HTMLInputElement).checked)}
        />
      </Flex>
    )
  }

  // The upgrade button is wider than the switch; inline beside the text it is
  // the item that shrinks and its label overflows, so it gets its own row.
  return (
    <Flex direction="column" align="start" gap="sm">
      {text}
      <Button size="sm" variant="outline" data-mol-id="settings-badge-upgrade" onClick={onUpgrade}>
        {t('settings.badge.upgrade', undefined, { defaultValue: 'Upgrade to hide' })}
      </Button>
    </Flex>
  )
}
